import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface SelectedItem {
  supplierId: string;
  /** Payable at the time it was selected — a fresh load clears the selection, so it can't go stale. */
  amount: number;
}

/**
 * What the Payments shell and its sub-route tabs share: the filters, the
 * pending settlement selection and a reload signal. Provided by the shell
 * component, so it lives exactly as long as the page and is reachable from the
 * routed tabs.
 *
 * Selection lives here (not in a tab) because one settlement can mix transfer
 * runs and manual contracts, and the "Record settlement" bar belongs to the
 * shell.
 */
@Injectable()
export class PaymentState {
  supplierId = '';
  fromDate = '';
  toDate = '';

  readonly runs = new Map<string, SelectedItem>();
  readonly contracts = new Map<string, SelectedItem>();

  private reloads = new Subject<void>();
  readonly reload$ = this.reloads.asObservable();

  filters() {
    return {
      supplierId: this.supplierId || undefined,
      fromDate: this.fromDate || undefined,
      toDate: this.toDate || undefined,
    };
  }

  get hasFilters(): boolean {
    return !!(this.supplierId || this.fromDate || this.toDate);
  }

  get selectedCount(): number {
    return this.runs.size + this.contracts.size;
  }

  get selectedTotal(): number {
    let total = 0;
    for (const item of this.runs.values()) total += item.amount;
    for (const item of this.contracts.values()) total += item.amount;
    return total;
  }

  /** A settlement covers exactly one supplier, so more than one here blocks recording it. */
  get selectedSupplierIds(): string[] {
    const ids = new Set<string>();
    for (const item of this.runs.values()) ids.add(item.supplierId);
    for (const item of this.contracts.values()) ids.add(item.supplierId);
    return [...ids];
  }

  clearSelection(): void {
    this.runs.clear();
    this.contracts.clear();
  }

  /** Reloads the visible tab. Data is about to change under the selection, so it is dropped. */
  reload(): void {
    this.clearSelection();
    this.reloads.next();
  }
}
